// PDP product form — add-to-cart submit wired to the cart store (emits CartAddEvent, opens drawer preview).

import { Component } from '@theme/component';
import { eventBus } from '@theme/events';
import {
	CartActions,
	CartAddEvent,
	CartPreviewOpenEvent,
} from '@theme/cart--state--actions';

/**
 * @typedef {Object} ProductFormRefs
 * @property {HTMLFormElement} form - The product form element
 * @property {HTMLButtonElement} submit - The submit button
 */

/**
 * Product Form Web Component
 *
 * Intercepts the native product form submit to add items via events (AJAX)
 * instead of a full page reload.
 *
 * Architecture (ngrx-style):
 * - Emits CartAddEvent (action) to request adding item to cart
 * - Emits CartPreviewOpenEvent (UI) to request drawer opens with preview
 * - Listens to cart:item-added and cart:add-failed (results) for button feedback
 *
 * Progressive enhancement: without JS the form submits normally to
 * Shopify's /cart/add endpoint.
 *
 * @attr {string} data-product-id - Product ID for event metadata
 * @attr {string} data-product-url - Product URL for preview link
 * @attr {string} data-product-title - Product title for preview
 * @attr {string} data-product-image - Featured image URL for preview
 *
 * @example
 * <product-form
 *   data-product-id="{{ product.id }}"
 *   data-product-url="{{ product.url }}"
 *   data-product-title="{{ product.title | escape }}"
 *   data-product-image="{{ product.featured_image | image_url: width: 150 }}"
 * >
 *   {% form 'product', product, ref: 'form' %}
 *     <select name="id">...</select>
 *     <input type="number" name="quantity" value="1" min="1">
 *     <button type="submit" ref="submit">Add to cart</button>
 *   {% endform %}
 * </product-form>
 *
 * @extends {Component<ProductFormRefs>}
 */
class ProductForm extends Component {
	/** @type {AbortController|null} */
	#abortController = null;

	/** @type {boolean} */
	#isSubmitting = false;

	/** @type {string|null} */
	#pendingVariantId = null;

	/** @type {(() => void)[]} */
	#unsubscribes = [];

	connectedCallback() {
		super.connectedCallback();

		if (!this.refs.form) return;

		this.#abortController = new AbortController();
		const { signal } = this.#abortController;

		this.refs.form.addEventListener('submit', (event) => {
			event.preventDefault();
			if (this.#isSubmitting) return;
			this.#handleSubmit();
		}, { signal });

		this.#setupResultListeners();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController?.abort();
		this.#abortController = null;
		this.#pendingVariantId = null;

		for (const unsub of this.#unsubscribes) {
			unsub();
		}
		this.#unsubscribes = [];
	}

	/**
	 * Set up listeners for result events to update button feedback.
	 */
	#setupResultListeners() {
		// Success: show success state
		this.#unsubscribes.push(
			eventBus.on(CartActions.itemAdded, (event) => {
				const { variantId } = event.detail.data;
				if (String(variantId) !== String(this.#pendingVariantId)) return;

				this.#setButtonState(this.refs.submit, 'success');
				setTimeout(() => this.#setButtonState(this.refs.submit, 'idle'), 2000);

				this.#isSubmitting = false;
				this.#pendingVariantId = null;
			})
		);

		// Failure: show error state
		this.#unsubscribes.push(
			eventBus.on(CartActions.addFailed, (event) => {
				const { variantId } = event.detail.data;
				if (String(variantId) !== String(this.#pendingVariantId)) return;

				console.error('ProductForm: Add to cart failed', event.detail.data.message);
				this.#setButtonState(this.refs.submit, 'error');
				setTimeout(() => this.#setButtonState(this.refs.submit, 'idle'), 3000);

				this.#isSubmitting = false;
				this.#pendingVariantId = null;
			})
		);
	}

	#handleSubmit() {
		const { form, submit } = this.refs;
		const variantId = form.elements.namedItem('id')?.value;
		if (!variantId) return;

		const quantity = parseInt(form.elements.namedItem('quantity')?.value, 10) || 1;

		this.#isSubmitting = true;
		this.#pendingVariantId = variantId;
		this.#setButtonState(submit, 'loading');

		// Extract preview data for optimistic UI
		const preview = this.#extractPreviewData();

		// 1. Emit action: request to add item
		eventBus.emit(
			new CartAddEvent({
				data: {
					variantId,
					quantity,
					source: 'product-page',
					productId: this.dataset.productId,
				},
			})
		);

		// 2. Emit UI event: request drawer opens with preview
		eventBus.emit(
			new CartPreviewOpenEvent({
				data: {
					preview,
				},
			})
		);
	}

	/**
	 * Build preview data from data attributes and the selected variant.
	 * @returns {{ title: string, price: string, imageSrc: string, productUrl: string, variantTitle: string }}
	 */
	#extractPreviewData() {
		const { form } = this.refs;
		const select = form.elements.namedItem('id');
		const selectedOption = select?.options?.[select.selectedIndex];

		return {
			title: this.dataset.productTitle ?? '',
			// Current price span only — the container also holds the struck
			// compare-at price when the product is on sale
			price:
				this.querySelector('[data-product-price] .product-price__current')?.textContent?.trim() ??
				this.querySelector('[data-product-price]')?.textContent?.trim() ??
				'',
			imageSrc: this.dataset.productImage ?? '',
			productUrl: this.dataset.productUrl ?? '',
			variantTitle: selectedOption?.textContent?.trim()?.split(' - ')[0] ?? '',
		};
	}

	/**
	 * Update the submit button's visual state.
	 * @param {HTMLElement|null} button
	 * @param {'idle'|'loading'|'success'|'error'} state
	 */
	#setButtonState(button, state) {
		if (!button) return;

		button.classList.remove(
			'product-form__submit--loading',
			'product-form__submit--success',
			'product-form__submit--error'
		);

		switch (state) {
			case 'loading':
				button.classList.add('product-form__submit--loading');
				button.setAttribute('aria-busy', 'true');
				button.disabled = true;
				break;
			case 'success':
				button.classList.add('product-form__submit--success');
				button.removeAttribute('aria-busy');
				button.disabled = false;
				break;
			case 'error':
				button.classList.add('product-form__submit--error');
				button.removeAttribute('aria-busy');
				button.disabled = false;
				break;
			default:
				button.removeAttribute('aria-busy');
				button.disabled = false;
		}
	}
}

if (!customElements.get('product-form')) {
	customElements.define('product-form', ProductForm);
}
